import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Paper } from '@mui/material';
import FormControl from '../../../../atomicDesign/atoms/FormControl';
import InputLabel from '../../../../atomicDesign/atoms/InputLabel';
import MenuItem from '../../../../atomicDesign/atoms/MenuItem';
import Box from '../../../../atomicDesign/atoms/Box';
import Stack from '../../../../atomicDesign/atoms/Stack';
import Select from '../../../../atomicDesign/atoms/Select';
import Text from '../../../../atomicDesign/atoms/Text';
import CalendarPicker from '../../../../atomicDesign/molecules/CalendarPicker';

import {
  getBuilding,
  getBuildings,
} from '../../../../controller/buildingModule/buildings';
import AdminContext from '../../../../context/admin';

import {
  createNaiveBaseline,
  getNaiveBaselineById,
  lockNaiveBaseline,
} from '../../../../controller/admin/baseline/naiveBaseline';

import BaselineChart from './chart/baselineChart';
import { buildBaselineChartDataFromPreview } from './chart/buildBaselineChartData';

import PreviewHeader from './components/header';
import MonthlySummarySection from './components/monthlySummary';
import WeeklySummarySection from './components/weeklySummary';
import Load from '../../../../atomicDesign/molecules/Load';
import Toast from '../../../../components/Toast';

import { monthKey } from './components/utils';
import type {
  Building,
  EnergyType,
  MonthCell,
  MonthRow,
  NaiveBaselinePreview,
  UiWeek,
  CreateNaiveBaselineData,
  MonthStatus,
  NaiveBaselineDraft,
  MergeParts,
  MonthDeviation,
} from './components/types';

import './datePicker.scss';
import DefaultButton from '../../../../atomicDesign/atoms/Buttons/DefaultButton';
import Confirm from '../../../../components/Confirm';
import AuditLog from './components/auditLog';
import IconButton from '../../../../atomicDesign/atoms/Buttons/IconButton';
import MuiFaIcon from '../../../../atomicDesign/atoms/MuiFaIcon';
import Tooltip from '../../../../atomicDesign/atoms/Tooltip';

import { createDegreeDays } from '../../../../controller/admin/degree_days/degreeDays';

type AdminContextType = {
  language: {
    settings: {
      baseline: {
        createNewBaselineConfirm: string;
        editBaseline: string;
        calculateBaseline: string;
        baselineChart: string;
        lockTooltip: {
          locked: string;
          unlocked: string;
        };
      };
      degreeDays: {
        error: string;
      };
      energyPrices: {
        building: string;
      };
      integrationOverview: {
        edit: {
          access: {
            dateFromLabel: string;
            dateToLabel: string;
          };
        };
      };
    };
  };
};

const toEnergyType = (x: string): EnergyType =>
  x === 'electricity' || x === 'districtHeating' || x === 'fuel'
    ? x
    : 'electricity';

export function thresholdChecker(
  preview: NaiveBaselinePreview,
  excluded: Set<string>
): Record<string, MonthDeviation> {
  const lower = 0.8;
  const upper = 1.2;

  const totalsByMonth: Record<number, number[]> = {};
  for (let m = 1; m <= 12; m++) totalsByMonth[m] = [];

  for (const y of preview.averagesPerMonth ?? []) {
    for (const m of y.months ?? []) {
      const key = monthKey(y.year, m.month);
      if (excluded.has(key)) continue;

      const total = (m.categorized ?? []).reduce(
        (sum, c) => sum + Number(c.total ?? 0),
        0
      );

      totalsByMonth[m.month].push(total);
    }
  }

  const meanByMonth: Record<number, number | null> = {};
  for (let month = 1; month <= 12; month++) {
    const values = totalsByMonth[month];
    meanByMonth[month] =
      values.length > 0
        ? values.reduce((a, b) => a + b, 0) / values.length
        : null;
  }

  const result: Record<string, MonthDeviation> = {};

  for (const y of preview.averagesPerMonth ?? []) {
    for (const m of y.months ?? []) {
      const key = monthKey(y.year, m.month);

      if (excluded.has(key)) {
        result[key] = { status: 'ok', deviationPct: null };
        continue;
      }

      const mean = meanByMonth[m.month];
      if (mean == null || mean === 0) {
        result[key] = { status: 'ok', deviationPct: null };
        continue;
      }

      const total = (m.categorized ?? []).reduce(
        (sum, c) => sum + Number(c.total ?? 0),
        0
      );

      const ratio = total / mean;
      const deviationPct = (ratio - 1) * 100;

      const status: MonthStatus =
        ratio < lower || ratio > upper ? 'partial' : 'ok';

      result[key] = { status, deviationPct };
    }
  }

  return result;
}

function convertToYearFirstOfMonthString(
  d: Date | null,
  forDegreeDays = false
): string | undefined {
  if (!d) return undefined;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return forDegreeDays ? `${y}${m}01` : `${y}-${m}-01`;
}

function buildPayload(
  data: NaiveBaselineDraft,
  startDate: Date | null,
  endDate: Date | null
): CreateNaiveBaselineData {
  const fallbackEndDate = endDate ?? new Date();

  return {
    _building: data._building!,
    _buildingName: data._buildingName,
    _vendor: data._vendor ?? '',
    startDate: convertToYearFirstOfMonthString(startDate),
    endDate: convertToYearFirstOfMonthString(fallbackEndDate),
  };
}

function mapPreview(raw: any): NaiveBaselinePreview {
  const r = raw as NaiveBaselinePreview;

  return {
    ...r,
    averagesPerMonth: (raw.averagesPerMonth ?? []).map((y: any) => ({
      year: y.year,
      months: (y.months ?? []).map((m: any) => ({
        month: m.month,
        excluded: m.excluded ?? false,
        categorized: (m.categorized ?? []).map(
          (
            c: NaiveBaselinePreview['averagesPerWeek'][string]['dayOfWeek'][string]['categorized'][number]
          ) => ({
            ...c,
            typeOfEnergy: toEnergyType(String(c.typeOfEnergy)),
            breakdown: (c.breakdown ?? []).map((b: any) => ({
              deviceTag: String(b.deviceTag),
              avg: Number(b.avg ?? 0),
              total: Number(b.total ?? 0),
              kWp: Number(b.kWp ?? 0),
              totalCo2: Number(b.totalCo2 ?? 0),
            })),
          })
        ),
      })),
    })),
    averagesPerWeek: Object.fromEntries(
      Object.entries(raw.averagesPerWeek ?? {}).map(([weekKey, week]: any) => [
        weekKey,
        {
          ...week,
          dayOfWeek: Object.fromEntries(
            Object.entries(week.dayOfWeek ?? {}).map(([dayKey, day]: any) => [
              dayKey,
              {
                ...day,
                categorized: (day.categorized ?? []).map(
                  (
                    c: NaiveBaselinePreview['averagesPerWeek'][string]['dayOfWeek'][string]['categorized'][number]
                  ) => ({
                    ...c,
                    typeOfEnergy: toEnergyType(String(c.typeOfEnergy)),
                    breakdown: (c.breakdown ?? []).map((b: any) => ({
                      deviceTag: String(b.deviceTag),
                      avg: Number(b.avg ?? 0),
                      total: Number(b.total ?? 0),
                      kWp: Number(b.kWp ?? 0),
                      totalCo2: Number(b.totalCo2 ?? 0),
                    })),
                  })
                ),
              },
            ])
          ),
        },
      ])
    ),
  };
}

function extractExcluded(preview: NaiveBaselinePreview) {
  const s = new Set<string>();
  for (const y of preview.averagesPerMonth ?? []) {
    for (const m of y.months ?? []) {
      if (m.excluded) s.add(monthKey(y.year, m.month));
    }
  }
  return s;
}

type Props = {
  data: NaiveBaselineDraft;
  setData: React.Dispatch<React.SetStateAction<NaiveBaselineDraft>>;
  initialPreview?: NaiveBaselinePreview;
};

export default function Baseline({ data, setData, initialPreview }: Props) {
  const { language } = useContext(AdminContext) as AdminContextType;
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [building, setBuilding] = useState<Building | null>(null);
  const [preview, setPreview] = useState<NaiveBaselinePreview | null>(
    () => initialPreview ?? null
  );

  const [appliedExcluded, setAppliedExcluded] = useState<Set<string>>(
    () => new Set()
  );
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  const [confirmation, setConfirmation] = useState<{
    show: boolean;
    message: string;
    onConfirm: null | (() => void);
    onCancel: null | (() => void);
  }>({
    show: false,
    message: '',
    onConfirm: null,
    onCancel: null,
  });

  const buildingId = data._building ?? '';
  const startDate = data.startDate ?? null;
  const endDate = data.endDate ?? null;

  const lastLoadedIdRef = useRef<string | null>(null);

  useEffect(() => {
    const fetchBuildings = async () => {
      const list = await getBuildings();
      setBuildings(list ?? []);
    };
    void fetchBuildings();
  }, []);

  const resetLocalBaselineState = () => {
    setPreview(null);
    setAppliedExcluded(new Set());
    setData((prev) => ({ ...prev, excludedMonths: [] }));
  };

  const syncExcludedToDraft = (s: Set<string>) => {
    setData((prev) => ({
      ...prev,
      excludedMonths: Array.from(s),
    }));
  };

  const applyLoadedBaselineToState = (raw: any) => {
    const mapped = mapPreview(raw);
    const nextApplied = extractExcluded(mapped);

    setData((prev) => ({
      ...prev,
      _id: raw?._id ? String(raw._id) : prev._id,
      _building: raw?._building ? String(raw._building) : prev._building,
      _buildingName: raw?._buildingName ?? prev._buildingName,
      _vendor: raw?._vendor ?? prev._vendor,
      startDate: raw?.startDate ? new Date(raw.startDate) : prev.startDate,
      endDate: raw?.endDate ? new Date(raw.endDate) : prev.endDate,
      active: typeof raw?.active === 'boolean' ? raw.active : prev.active,
      locked: typeof raw?.locked === 'boolean' ? raw.locked : prev.locked,
      excludedMonths: Array.from(nextApplied),
    }));

    setPreview(mapped);
    setAppliedExcluded(nextApplied);
  };

  const loadBaselineById = async (id: string) => {
    if (!id) return;

    if (lastLoadedIdRef.current === id) return;

    try {
      setLoading(true);
      lastLoadedIdRef.current = id;

      const raw = await getNaiveBaselineById(id);
      applyLoadedBaselineToState(raw);
    } catch (e) {
      console.error('Failed to load baseline by id:', e);
      lastLoadedIdRef.current = null;
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = async (mode: 'automaticFetch' | 'overwrite') => {
    if (!data._building) {
      resetLocalBaselineState();
      return;
    }

    try {
      setLoading(true);

      const payload = buildPayload(data, startDate, endDate);
      let raw: any;

      if (mode === 'overwrite') {
        raw = await createNaiveBaseline(payload);

        const createdId = raw?._id ? String(raw._id) : undefined;
        lastLoadedIdRef.current = createdId ?? null;

        applyLoadedBaselineToState(raw);
        return;
      }

      if (!data?._id) {
        resetLocalBaselineState();
        return;
      }

      raw = await getNaiveBaselineById(data._id);
      lastLoadedIdRef.current = String(data._id);
      applyLoadedBaselineToState(raw);
    } catch (e: any) {
      const message =
        e?.response?.data?.message ??
        'Could not calculate the baseline. Please try again.';
      setToast({ message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!data._id) return;
    void loadBaselineById(String(data._id));
  }, [data._id]);

  useEffect(() => {
    if (initialPreview) {
      const nextApplied = extractExcluded(initialPreview);
      setAppliedExcluded(nextApplied);
      syncExcludedToDraft(nextApplied);
      setPreview(initialPreview);
      return;
    }

    if (data._id) return;

    void loadPreview('automaticFetch');
  }, [initialPreview, data._building, data._id]);

  const openFetchConfirm = () => {
    const lat = building?.gps?.lat;
    const lng = building?.gps?.long;
    if (lat == null || lng == null || (lat === 0 && lng === 0)) {
      setToast({
        message:
          language?.settings?.degreeDays?.error ??
          'Building is missing GPS coordinates',
        type: 'error',
      });
      return;
    }
    if (preview) {
      setConfirmation({
        show: true,
        message:
          language?.settings?.baseline?.createNewBaselineConfirm ??
          'Create baseline? This will overwrite the current baseline',
        onConfirm: () => {
          setConfirmation((c) => ({ ...c, show: false }));
          void loadPreview('overwrite');
          createDegreeDays(
            building?.gps?.lat ?? 0,
            building?.gps?.long ?? 0,
            startDate ? convertToYearFirstOfMonthString(startDate, true)! : '',
            endDate ? convertToYearFirstOfMonthString(endDate, true)! : '',
            buildingId
          ).catch((err) => {
            console.error('Failed to create degree days:', err);
          });
        },
        onCancel: () => setConfirmation((c) => ({ ...c, show: false })),
      });
    } else {
      void loadPreview('overwrite');
      createDegreeDays(
        building?.gps?.lat ?? 0,
        building?.gps?.long ?? 0,
        startDate ? convertToYearFirstOfMonthString(startDate, true)! : '',
        endDate ? convertToYearFirstOfMonthString(endDate, true)! : '',
        buildingId
      ).catch((err) => {
        console.error('Failed to create degree days:', err);
      });
    }
  };

  useEffect(() => {
    if (!buildingId) {
      setBuilding(null);
      return;
    }
    const fetchBuilding = async () => {
      try {
        const building = await getBuilding(buildingId);
        setBuilding(building);
      } catch (e) {
        console.error('Failed to fetch building details:', e);
      }
    };

    fetchBuilding();
  }, [buildingId]);

  const monthStatusByKey = useMemo(() => {
    if (!preview) return {};
    return thresholdChecker(preview, appliedExcluded);
  }, [preview, appliedExcluded]);

  const monthCoverage: MonthCell[] = useMemo(() => {
    if (!preview) return [];

    const years = preview.averagesPerMonth
      .map((y) => y.year)
      .sort((a, b) => a - b);
    if (years.length === 0) return [];

    const minYear = years[0];
    const maxYear = years[years.length - 1];

    const existing = new Set(
      preview.averagesPerMonth.flatMap((y) =>
        y.months.map((m) => monthKey(y.year, m.month))
      )
    );

    const cells: MonthCell[] = [];
    for (let year = minYear; year <= maxYear; year++) {
      for (let month = 1; month <= 12; month++) {
        const key = monthKey(year, month);
        const hasData = existing.has(key);

        cells.push({
          year,
          month,
          excluded: appliedExcluded.has(key),
          status: hasData ? (monthStatusByKey[key].status ?? 'ok') : 'missing',
          reasons: hasData ? [] : ['No data for month'],
        });
      }
    }

    return cells;
  }, [preview, appliedExcluded, monthStatusByKey]);

  const yearsInCoverage = useMemo(
    () =>
      Array.from(new Set(monthCoverage.map((c) => c.year))).sort(
        (a, b) => a - b
      ),
    [monthCoverage]
  );

  const weeks = useMemo((): Array<[string, UiWeek]> => {
    if (!preview) return [];
    return Object.entries(preview.averagesPerWeek ?? {});
  }, [preview]);

  const monthRows: MonthRow[] = useMemo(() => {
    if (!preview) return [];

    return preview.averagesPerMonth.flatMap((y) =>
      y.months.flatMap((m) => {
        const key = monthKey(y.year, m.month);
        const excluded = appliedExcluded.has(key);

        return m.categorized.map((c) => ({
          id: `${y.year}-${m.month}-${String(c.typeOfEnergy)}`,
          year: y.year,
          month: m.month,
          excluded,
          typeOfEnergy: String(c.typeOfEnergy),
          avg: Number(c.avg ?? 0),
          total: Number(c.total ?? 0),
          kWp: Number(c.kWp ?? 0),
          totalCo2: Number(c.totalCo2 ?? 0),
          breakdownCount: c.breakdown?.length ?? 0,
        }));
      })
    );
  }, [preview, appliedExcluded]);

  const { chart } = useMemo(() => {
    if (!preview) return { chart: null as any };
    return buildBaselineChartDataFromPreview({
      preview,
      excluded: appliedExcluded,
    });
  }, [preview, appliedExcluded]);

  const toggleExcludedMonth = (key: string) => {
    setAppliedExcluded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);

      syncExcludedToDraft(next);
      return next;
    });
  };

  const mergeDate = (
    prevDate: Date | null | undefined,
    parts: MergeParts
  ): Date => {
    const base = prevDate ?? new Date();
    const year = parts.year ?? base.getFullYear();
    const month = parts.month ?? base.getMonth();
    return new Date(year, month, 1);
  };

  const baselineStart = useMemo(() => {
    const first = preview?.averagesPerMonth[0];
    if (!first) return undefined;
    return { year: first.year, month: first.months[0].month };
  }, [preview]);

  const baselineEnd = useMemo(() => {
    const last = preview?.averagesPerMonth[preview.averagesPerMonth.length - 1];
    if (!last) return undefined;
    return {
      year: last.year,
      month: last.months[last.months.length - 1].month,
    };
  }, [preview]);

  const handleLockToggle = async () => {
    if (!data._id) return;

    const nextLocked = !data.locked;

    try {
      setLoading(true);
      const updated = await lockNaiveBaseline(String(data._id), nextLocked);
      applyLoadedBaselineToState(updated);
    } catch (e: any) {
      const message =
        e?.response?.data?.message ??
        'Could not update lock status. Please try again.';
      setToast({ message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {confirmation.show && (
        <Confirm
          message={confirmation.message}
          onConfirm={() => confirmation.onConfirm?.()}
          onCancel={() => confirmation.onCancel?.()}
        />
      )}

      {data._id ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Stack direction="row" alignItems="center" alignContent="center">
            <Text variant="h6" fontWeight={600}>
              {language?.settings?.baseline?.editBaseline ??
                'Edit Baseline for'}{' '}
              {data._buildingName}
            </Text>

            <Tooltip
              title={
                data.locked
                  ? (language?.settings?.baseline?.lockTooltip?.locked ??
                    'Baseline is locked. Click to unlock and allow automatic edits.')
                  : (language?.settings?.baseline?.lockTooltip?.unlocked ??
                    'Lock baseline to prevent automatic edits')
              }
            >
              <IconButton
                onClick={() => {
                  void handleLockToggle();
                }}
                disabled={loading}
              >
                <MuiFaIcon
                  name={
                    data.locked ? 'fa-light fa-lock' : 'fa-light fa-lock-open'
                  }
                  style={{ color: data.locked ? '#d32f2f' : '#388e3c' }}
                />
              </IconButton>
            </Tooltip>
          </Stack>

          <Box sx={{ minWidth: 280 }}>
            <AuditLog
              _building={data._building ?? ''}
              baselineId={data._id ?? ''}
              onChangeBaselineId={(id) =>
                setData((prev) => ({ ...prev, _id: id }))
              }
            />
          </Box>
        </Box>
      ) : (
        <FormControl>
          <InputLabel id="building-label">
            {language?.settings?.energyPrices?.building ?? 'Building'}
          </InputLabel>

          <Select
            value={buildingId}
            setValue={(value) => {
              const nextBuildingId = String(value);
              const selected = buildings.find((b) => b._id === nextBuildingId);

              setData((prev) => ({
                ...prev,
                _id: undefined,
                _building: nextBuildingId,
                _vendor: selected?._vendor,
                _buildingName: selected?.name,
              }));

              lastLoadedIdRef.current = null;
            }}
            label={language?.settings?.energyPrices?.building ?? 'Building'}
          >
            {buildings.map((b) => (
              <MenuItem key={b._id} value={b._id}>
                {b.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Paper sx={{ p: 2, borderRadius: 0 }} variant="outlined">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems="stretch"
        >
          {/* Left */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <PreviewHeader
              monthCoverage={monthCoverage}
              yearsInCoverage={yearsInCoverage}
              excluded={appliedExcluded}
              onToggleExcluded={toggleExcludedMonth}
              Loading={loading}
              monthStatusByKey={monthStatusByKey}
              baselineStart={baselineStart}
              baselineEnd={baselineEnd}
              disabled={data.locked === true && data.active === true}
            />
          </Box>

          <Box
            sx={{
              width: { xs: '100%', md: 280 },
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: '100%',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              {/* From */}
              <Box sx={{ width: '100%', textAlign: 'center' }}>
                <Text variant="caption" sx={{ fontWeight: 600, mb: 1 }}>
                  {language?.settings?.integrationOverview?.edit?.access
                    ?.dateFromLabel ?? 'From Date'}
                </Text>

                <Stack direction="row" spacing={1} justifyContent="center">
                  <CalendarPicker
                    date={startDate ?? new Date()}
                    onChange={(date) =>
                      setData((prev) => ({ ...prev, startDate: date }))
                    }
                    datePickerProps={{
                      showYearPicker: true,
                      dateFormat: 'YYYY',
                      shouldCloseOnSelect: true,
                      calendarClassName: 'month-year-picker-clean',
                    }}
                    displayOptions={{ year: 'numeric' }}
                    maxDate={endDate || new Date()}
                  />

                  <CalendarPicker
                    date={startDate ?? new Date()}
                    onChange={(date) =>
                      setData((prev) => ({ ...prev, startDate: date }))
                    }
                    datePickerProps={{
                      showMonthYearPicker: true,
                      dateFormat: 'MMM',
                      shouldCloseOnSelect: true,
                      calendarClassName: 'month-year-picker-clean',
                    }}
                    displayOptions={{ month: 'short' }}
                    maxDate={endDate || new Date()}
                  />
                </Stack>
              </Box>

              {/* To */}
              <Box sx={{ width: '100%', textAlign: 'center' }}>
                <Text variant="caption" sx={{ fontWeight: 600, mb: 1 }}>
                  {language?.settings?.integrationOverview?.edit?.access
                    ?.dateToLabel ?? 'To Date'}
                </Text>

                <Stack direction="row" spacing={1} justifyContent="center">
                  <CalendarPicker
                    date={endDate ?? new Date()}
                    onChange={(date: Date | null) => {
                      if (!date) return;
                      setData((prev) => ({
                        ...prev,
                        endDate: mergeDate(prev.endDate, {
                          year: date.getFullYear(),
                        }),
                      }));
                    }}
                    datePickerProps={{
                      showYearPicker: true,
                      dateFormat: 'yyyy',
                      shouldCloseOnSelect: true,
                      calendarClassName: 'month-year-picker-clean',
                    }}
                    displayOptions={{ year: 'numeric' }}
                    maxDate={new Date()}
                    minDate={startDate || new Date()}
                  />

                  <CalendarPicker
                    date={endDate ?? new Date()}
                    onChange={(date: Date | null) => {
                      if (!date) return;
                      setData((prev) => ({
                        ...prev,
                        endDate: mergeDate(prev.endDate, {
                          month: date.getMonth(),
                        }),
                      }));
                    }}
                    datePickerProps={{
                      showMonthYearPicker: true,
                      dateFormat: 'MMM',
                      shouldCloseOnSelect: true,
                      calendarClassName: 'month-year-picker-clean',
                    }}
                    displayOptions={{ month: 'short' }}
                    maxDate={new Date()}
                    minDate={startDate || new Date()}
                  />
                </Stack>
              </Box>

              {/* Button */}
              <DefaultButton
                onClick={openFetchConfirm}
                disabled={
                  loading ||
                  !data._building ||
                  !startDate ||
                  (data.locked === true && data.active === true)
                }
                sx={{
                  height: 36,
                  px: 3,
                }}
              >
                {language?.settings?.baseline?.calculateBaseline ??
                  'Calculate Baseline'}
              </DefaultButton>
            </Box>
          </Box>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Text variant="h6" sx={{ mb: 1 }}>
          {language?.settings?.baseline?.baselineChart ?? 'Baseline chart'}
        </Text>
        {chart && <BaselineChart baseline={chart} />}
      </Paper>

      <MonthlySummarySection
        monthRows={monthRows}
        buildingName={data._buildingName}
      />
      <WeeklySummarySection weeks={weeks} buildingName={data._buildingName} />
      {loading && <Load />}

      <Toast
        message={toast?.message}
        type={toast?.type}
        close={() => setToast(null)}
      />
    </Box>
  );
}
